// Historial de pagos: reporte de pagos y salidas de dinero por periodo.
// Filtros → Resumen → Detalle → PDF / Imprimir (el PDF respeta exactamente los filtros de la pantalla).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { getReportePagos } from '../../services/data';
import { useSucursalActiva } from '../../context/SucursalActivaContext';
import { toMoney } from '../../utils/numbers';
import PeriodoFiltro, { usePeriodo } from '../cierres/PeriodoFiltro';
import { exportReportePagosPdf, fechaHoraMov } from '../../pdf/reportePagos';
import { ComprobanteLink } from '../finanzas/Comprobante';
import '../finanzas/ui.css';
import '../proveedores/Proveedores.css';
import FondoModal from '../common/FondoModal';

const GRUPOS = [['proveedor', 'Pagos a proveedores'], ['gasto', 'Gastos'], ['otro', 'Otros pagos']];
const nombreGrupo = Object.fromEntries(GRUPOS);
const sinAcentos = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const round = (v) => Math.round(v * 100) / 100;
const unicos = (arr) => [...new Set(arr.filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es'));

function resumir(movs) {
  const suma = (g) => round(movs.filter((m) => !g || m.grupo === g).reduce((s, m) => s + m.monto, 0));
  return { total: suma(), proveedor: suma('proveedor'), gasto: suma('gasto'), otro: suma('otro'), cantidad: movs.length };
}

export default function HistorialPagos() {
  const { sucursalId, sucursales, me } = useSucursalActiva();
  const per = usePeriodo('hoy');
  const [rep, setRep] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [tipo, setTipo] = useState('');
  const [proveedor, setProveedor] = useState('');
  const [beneficiario, setBeneficiario] = useState('');
  const [forma, setForma] = useState('');
  const [pagador, setPagador] = useState('');
  const [referencia, setReferencia] = useState('');
  const [detalle, setDetalle] = useState(null);

  const cargar = useCallback(async () => {
    if (!sucursalId) return;
    setCargando(true);
    try { setRep(await getReportePagos({ desde: per.desde, hasta: per.hasta, sucursalId })); }
    catch (e) { setRep(null); Swal.fire('Error', e.message || 'No se pudo cargar el reporte.', 'error'); }
    finally { setCargando(false); }
  }, [sucursalId, per.desde, per.hasta]);
  useEffect(() => { cargar(); }, [cargar]);

  const sucActual = sucursalId;
  const todas = sucActual === 'all';
  const nombreSuc = todas ? 'Todas las sucursales' : (sucursales.find((s) => s.id === sucActual)?.nombre || '');
  const movs = useMemo(() => rep?.movimientos || [], [rep]);
  const delTipo = useMemo(() => movs.filter((m) => !tipo || (tipo.startsWith('g:') ? m.grupo === tipo.slice(2) : m.tipo === tipo.slice(2))), [movs, tipo]);
  // Proveedor solo aplica a pagos a proveedores; beneficiario solo a gastos y otros pagos.
  const grupoTipo = tipo.startsWith('g:') ? tipo.slice(2) : (tipo ? movs.find((m) => m.tipo === tipo.slice(2))?.grupo : '');
  const verProveedor = grupoTipo === 'proveedor';
  const verBeneficiario = grupoTipo === 'gasto' || grupoTipo === 'otro';
  const opciones = useMemo(() => ({ tipos: unicos(movs.map((m) => m.tipo)), formas: unicos(delTipo.map((m) => m.formaPago)),
    proveedores: unicos(delTipo.filter((m) => m.grupo === 'proveedor').map((m) => m.beneficiario)),
    beneficiarios: unicos(delTipo.filter((m) => m.grupo !== 'proveedor').map((m) => m.beneficiario)) }), [movs, delTipo]);
  const filtrados = useMemo(() => {
    const qb = sinAcentos(beneficiario.trim()), qr = sinAcentos(referencia.trim());
    return delTipo.filter((m) => (!forma || m.formaPago === forma)
      && (!verProveedor || !proveedor || m.beneficiario === proveedor)
      && (!verBeneficiario || !qb || sinAcentos(m.beneficiario).includes(qb))
      && (!pagador || (m.pagadoPor || 'Sucursal') === pagador)
      && (!qr || sinAcentos(`${m.referencia} ${m.descripcion}`).includes(qr)));
  }, [delTipo, forma, proveedor, beneficiario, referencia, pagador, verProveedor, verBeneficiario]);
  const r = resumir(filtrados);
  const hayFiltros = tipo || forma || pagador || proveedor || beneficiario.trim() || referencia.trim();
  const cambiarTipo = (v) => { setTipo(v); setProveedor(''); setBeneficiario(''); setForma(''); };

  const pdf = (imprimir) => exportReportePagosPdf({ desde: per.desde, hasta: per.hasta, movimientos: filtrados, resumen: r }, {
    sucursal: nombreSuc, todas, generadoPor: me?.username,
    filtros: [tipo && `Tipo: ${tipo.startsWith('g:') ? nombreGrupo[tipo.slice(2)] : tipo.slice(2)}`,
      verProveedor && proveedor && `Proveedor: ${proveedor}`, verBeneficiario && beneficiario.trim() && `Beneficiario: ${beneficiario.trim()}`,
      forma && `Forma de pago: ${forma}`, pagador && `Pagado por: ${pagador}`, referencia.trim() && `Referencia / factura: ${referencia.trim()}`].filter(Boolean),
  }, { imprimir });

  return (
    <div className="prov-shell">
      <header className="prov-header">
        <div><h1>Pagos registrados</h1><p style={{ margin: 0, color: 'var(--ink-muted)' }}>Pagos y salidas de dinero del periodo · {nombreSuc}</p></div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="fx-btn" disabled={!rep || cargando} onClick={() => pdf(true)}>Imprimir</button>
          <button type="button" className="fx-btn fx-btn--primary" disabled={!rep || cargando} onClick={() => pdf(false)}>Descargar PDF</button>
        </div>
      </header>

      <PeriodoFiltro p={per}>
        <label>Tipo
          <select value={tipo} onChange={(e) => cambiarTipo(e.target.value)}>
            <option value="">Todos</option>
            <optgroup label="Grupo">{GRUPOS.map(([id, l]) => <option key={id} value={`g:${id}`}>{l}</option>)}</optgroup>
            {opciones.tipos.length > 0 && <optgroup label="Tipo específico">{opciones.tipos.map((t) => <option key={t} value={`t:${t}`}>{t}</option>)}</optgroup>}
          </select>
        </label>
        {verProveedor && <label>Proveedor
          <select value={proveedor} onChange={(e) => setProveedor(e.target.value)}>
            <option value="">Todos</option>{opciones.proveedores.map((b) => <option key={b}>{b}</option>)}
          </select>
        </label>}
        {verBeneficiario && <label>Beneficiario
          <input list="hp-beneficiarios" value={beneficiario} onChange={(e) => setBeneficiario(e.target.value)} placeholder="Todos" />
          <datalist id="hp-beneficiarios">{opciones.beneficiarios.map((b) => <option key={b} value={b} />)}</datalist>
        </label>}
        <label>Forma de pago
          <select value={forma} onChange={(e) => setForma(e.target.value)}>
            <option value="">Todas</option>{opciones.formas.map((f) => <option key={f}>{f}</option>)}
          </select>
        </label>
        <label>Pagado por<select value={pagador} onChange={(e) => setPagador(e.target.value)}><option value="">Todos</option>
          <option>Sucursal</option><option>Propietaria</option><option>Cuenta bancaria del negocio</option></select></label>
        <label>Referencia / factura<input type="search" value={referencia} onChange={(e) => setReferencia(e.target.value)} placeholder="No. de factura o referencia" /></label>
        {hayFiltros && <button type="button" className="fx-link" onClick={() => { cambiarTipo(''); setReferencia(''); setPagador(''); }}>Limpiar filtros</button>}
      </PeriodoFiltro>

      <div className="fx-grid">
        <div className="fx-card fx-stat"><span>Total pagado</span><b>{toMoney(r.total)}</b></div>
        <div className="fx-card fx-stat"><span>Pagos a proveedores</span><b>{toMoney(r.proveedor)}</b></div>
        <div className="fx-card fx-stat"><span>Gastos</span><b>{toMoney(r.gasto)}</b></div>
        <div className="fx-card fx-stat"><span>Otros pagos</span><b>{toMoney(r.otro)}</b></div>
        <div className="fx-card fx-stat"><span>Cantidad de movimientos</span><b>{r.cantidad}</b></div>
      </div>

      <div className="fx-card">
        {cargando ? <p className="fx-empty">Cargando…</p> : !filtrados.length ? <p className="fx-empty">Sin pagos con los filtros seleccionados.</p> : (
          <table className="fx-table fx-table--stack">
            <thead><tr><th>Fecha y hora</th>{todas && <th>Sucursal</th>}<th>Tipo</th><th>Beneficiario / proveedor</th><th>Descripción</th>
              <th>Forma de pago</th><th>Pagado por</th><th>Referencia</th><th className="num">Monto</th><th>Acción</th></tr></thead>
            <tbody>{filtrados.map((m) => (
              <tr key={m.id}>
                <td data-label="Fecha y hora">{fechaHoraMov(m)}</td>
                {todas && <td data-label="Sucursal">{m.sucursal}</td>}
                <td data-label="Tipo">{m.tipo}</td>
                <td data-label="Beneficiario / proveedor">{m.beneficiario || '—'}</td>
                <td data-label="Descripción">{m.descripcion || '—'}</td>
                <td data-label="Forma de pago">{m.formaPago}</td>
                <td data-label="Pagado por">{m.pagadoPor || 'Sucursal'}</td>
                <td data-label="Referencia">{m.referencia || '—'}</td>
                <td data-label="Monto" className="num"><b>{toMoney(m.monto)}</b></td>
                <td><button type="button" className="fx-link" onClick={() => setDetalle(m)}>Ver detalle</button></td>
              </tr>))}</tbody>
            <tfoot><tr><td colSpan={todas ? 8 : 7} style={{ textAlign: 'right' }}><b>Total general</b></td><td className="num"><b>{toMoney(r.total)}</b></td><td /></tr></tfoot>
          </table>)}
      </div>

      {detalle && <DetallePago m={detalle} onClose={() => setDetalle(null)} />}
    </div>
  );
}

function DetallePago({ m, onClose }) {
  const filas = [['Fecha y hora', fechaHoraMov(m)], ['Sucursal', m.sucursal], ['Grupo', nombreGrupo[m.grupo]], ['Tipo', m.tipo],
    ['Beneficiario / proveedor', m.beneficiario], ['Descripción', m.descripcion], ['Forma de pago', m.formaPago], ['Referencia', m.referencia],
    ['Pagado por', m.pagadoPor || 'Sucursal'], ['Observación', m.observacion], ['Origen', [m.origen, m.turno && `turno ${m.turno}`].filter(Boolean).join(' · ')], ['Registró', m.registradoPor]];
  return (
    <FondoModal onClose={onClose}>
      <section className="fx-modal fx-modal--sm" role="dialog" aria-modal="true" aria-label="Detalle del pago" onMouseDown={(e) => e.stopPropagation()}>
        <header><div><h2>Detalle del pago</h2><p>{m.tipo} · <b>{toMoney(m.monto)}</b></p></div><button type="button" aria-label="Cerrar" onClick={onClose}>×</button></header>
        <table className="fx-table"><tbody>
          {filas.filter(([, v]) => v).map(([k, v]) => <tr key={k}><td style={{ color: 'var(--ink-muted)', fontWeight: 600, width: '40%' }}>{k}</td><td>{v}</td></tr>)}
          <tr><td style={{ color: 'var(--ink-muted)', fontWeight: 600 }}>Monto</td><td><b>{toMoney(m.monto)}</b></td></tr>
          {m.archivoUrl && <tr><td style={{ color: 'var(--ink-muted)', fontWeight: 600 }}>Comprobante</td><td><ComprobanteLink url={m.archivoUrl} texto="Abrir" /></td></tr>}
        </tbody></table>
        <div className="fx-actions"><button type="button" className="fx-btn" onClick={onClose}>Cerrar</button></div>
      </section>
    </FondoModal>
  );
}
